<template>
  <div class="chat-card" :aria-label="labelsState['lv_demo_aria_label'] || 'Live assistant demo'">
    <div class="channels" role="tablist" :aria-label="labelsState['lv_demo_channels_label'] || 'Channels'">
      <button
        v-for="(c, key) in channelDefs"
        :key="key"
        role="tab"
        :aria-selected="key === channel"
        :class="{ active: key === channel }"
        @click="setChannel(key)"
      >
        <img
          v-if="c.domain"
          :src="brandIcon(c.domain)"
          :alt="c.label"
          class="channel-tab-icon"
          @error="$event.target.style.display='none'"
        />
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20M12 2c2.5 3 4 6.5 4 10s-1.5 7-4 10c-2.5-3-4-6.5-4-10s1.5-7 4-10z"/>
        </svg>
        {{ c.label }}
      </button>
    </div>

    <div class="chat-head">
      <div
        class="chat-avatar"
        :style="{ background: `linear-gradient(135deg, ${channelMeta.color}, var(--violet))` }"
      >NQ</div>
      <div>
        <div class="chat-name">{{ channelMeta.agent }}</div>
        <div class="chat-meta">{{ labelsState['lv_demo_status_online'] || 'Online · Responds in < 1s' }}</div>
      </div>
      <span
        class="channel-pill"
        :style="{
          color: channelMeta.color,
          borderColor: channelMeta.color + '55',
          background: channelMeta.color + '11'
        }"
      >{{ labelsState['lv_demo_status_badge'] || 'AI · 24/7' }}</span>
    </div>

    <div class="chat-body" ref="chatBody">
      <template v-for="(m, i) in visibleMessages">
        <div :key="'msg-' + i" :class="['msg', m.from]">{{ m.text }}</div>
        <div
          v-if="m.quick && i === step - 1"
          :key="'quick-' + i"
          class="chat-quick"
        >
          <button v-for="q in m.quick" :key="q" type="button">{{ q }}</button>
        </div>
      </template>
      <div v-if="showTyping" class="msg typing">
        <span></span><span></span><span></span>
      </div>
    </div>

    <form class="chat-input" @submit.prevent>
      <input :placeholder="labelsState['lv_demo_input_placeholder'] || 'Type a message...'" :aria-label="labelsState['lv_demo_input_placeholder'] || 'Mensaje'" />
      <button type="submit" :aria-label="labelsState['lv_demo_send_label'] || 'Send'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z"/>
        </svg>
      </button>
    </form>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue';
import { useLabelsStore } from '@/stores/labels';
import { useLanguageStore } from '@/stores/language';

const CLIENT_ID = '1idtZELcktsTTUyKkcX';

export default {
  name: 'ChatDemoV2',
  setup() {
    const labelsStore = useLabelsStore();
    const languageStore = useLanguageStore();
    onMounted(() => labelsStore.fetchAndSetLabels(languageStore.language));
    return { labelsState: computed(() => labelsStore.labels) };
  },
  data() {
    return {
      channel: 'whatsapp',
      step: 0,
      showTyping: false,
      timer: null,
    };
  },
  computed: {
    channelDefs() {
      const l = this.labelsState;
      return {
        whatsapp:  { label: 'WhatsApp',  color: '#00cc99', agent: l['lv_demo_whatsapp_agent']  || 'NQA Assistant · WhatsApp Business', domain: 'whatsapp.com' },
        instagram: { label: 'Instagram', color: '#60a5fa', agent: l['lv_demo_instagram_agent'] || '@nqartificial · Direct Messages',  domain: 'instagram.com' },
        messenger: { label: 'Messenger', color: '#2563eb', agent: l['lv_demo_messenger_agent'] || 'NQA · Messenger',                    domain: 'messenger.com' },
        web:       { label: 'Web',       color: '#22d3ee', agent: l['lv_demo_web_agent']       || 'NQA Assistant · Website',          domain: null },
      };
    },
    scripts() {
      const l = this.labelsState;
      return {
        whatsapp: [
          { from: 'user', text: l['lv_demo_whatsapp_user_one'] || 'Hi! Do you have appointments available tomorrow?' },
          { from: 'bot',  text: l['lv_demo_whatsapp_bot_one']  || 'Hi Sofia! 👋 I have availability tomorrow at 10:00, 14:30 and 17:00. Which do you prefer?', quick: ['10:00', '14:30', '17:00'] },
          { from: 'user', text: l['lv_demo_whatsapp_user_two'] || '14:30 works for me' },
          { from: 'bot',  text: l['lv_demo_whatsapp_bot_two']  || 'Done, I scheduled your appointment for tomorrow at 14:30. I will send you a reminder 1 hour before. Anything else?' },
        ],
        instagram: [
          { from: 'user', text: l['lv_demo_instagram_user_one']   || 'I saw the product in stories, what is the price?' },
          { from: 'bot',  text: l['lv_demo_instagram_bot_one']    || 'Hi! It costs $189.000 with free shipping. Want to reserve it? I will add it to the cart 🛒',
            quick: [l['lv_demo_instagram_quick_one'] || 'Buy now', l['lv_demo_instagram_quick_two'] || 'More photos', l['lv_demo_instagram_quick_three'] || 'Talk to agent'] },
          { from: 'user', text: l['lv_demo_instagram_user_two']   || 'Buy now' },
          { from: 'bot',  text: l['lv_demo_instagram_bot_two']    || 'Perfect. I am sending you a secure payment link and 24h shipping options ✨' },
        ],
        messenger: [
          { from: 'user', text: l['lv_demo_messenger_user_one'] || 'What about my order #4821?' },
          { from: 'bot',  text: l['lv_demo_messenger_bot_one']  || 'I found your order. Status: on route 🚚 — estimated delivery today between 16:00 and 18:00.' },
          { from: 'user', text: l['lv_demo_messenger_user_two'] || 'Thank you!' },
          { from: 'bot',  text: l['lv_demo_messenger_bot_two']  || 'My pleasure 💜 I will let you know when the courier is 5 min away.' },
        ],
        web: [
          { from: 'user', text: l['lv_demo_web_user_one']   || 'What plans do you have?' },
          { from: 'bot',  text: l['lv_demo_web_bot_one']    || 'We have plans from $49 USD/month. How many messages do you receive per month?',
            quick: [l['lv_demo_web_quick_one'] || '< 1,000', l['lv_demo_web_quick_two'] || '1k – 10k', l['lv_demo_web_quick_three'] || '> 10k'] },
          { from: 'user', text: l['lv_demo_web_user_two']   || 'About 3,000' },
          { from: 'bot',  text: l['lv_demo_web_bot_two']    || 'We recommend the Pro plan: multi-channel, human handover and real-time analytics. Shall we schedule a demo?' },
        ],
      };
    },
    script() {
      return this.scripts[this.channel];
    },
    channelMeta() {
      return this.channelDefs[this.channel];
    },
    visibleMessages() {
      return this.script.slice(0, this.step);
    },
  },
  watch: {
    channel() {
      this.clearTimer();
      this.step = 0;
      this.showTyping = false;
      this.$nextTick(() => this.scheduleNext());
    },
  },
  mounted() {
    this.scheduleNext();
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    scheduleNext() {
      this.clearTimer();
      this.scrollToBottom();

      if (this.step >= this.script.length) {
        this.timer = setTimeout(() => {
          this.step = 0;
          this.scheduleNext();
        }, 4000);
        return;
      }

      const next = this.script[this.step];
      if (next.from === 'bot') {
        this.showTyping = true;
        this.scrollToBottom();
        this.timer = setTimeout(() => {
          this.showTyping = false;
          this.step++;
          this.scheduleNext();
        }, 1100);
      } else {
        this.timer = setTimeout(() => {
          this.step++;
          this.scheduleNext();
        }, 1400);
      }
    },
    setChannel(key) {
      this.channel = key;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatBody) {
          this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight;
        }
      });
    },
    brandIcon(domain) {
      return `https://cdn.brandfetch.io/${domain}/w/128/h/128/icon?c=${CLIENT_ID}`;
    },
  },
};
</script>

<style scoped>
.channel-tab-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  border-radius: 4px;
  display: block;
  flex-shrink: 0;
}
</style>
